'use client'

import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { homeFaqs, type HomeFaq } from '@/lib/copy/faqs'
import { SectionEyebrow } from '@/components/marketing/section-eyebrow'

// Framer-inspired FAQ: search + category chip filter + numbered rows.
// Categories are derived from the data, "All" is always first. Search
// matches against both question and answer text (case-insensitive).
// Empty-state shows when the query has no matches.

type FilterKey = 'All' | HomeFaq['category']

export function Faq() {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState<FilterKey>('All')

  const categories = useMemo<FilterKey[]>(() => {
    const seen = new Set<HomeFaq['category']>()
    for (const f of homeFaqs) seen.add(f.category)
    return ['All', ...seen]
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return homeFaqs.filter((f) => {
      if (active !== 'All' && f.category !== active) return false
      if (!q) return true
      return f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    })
  }, [query, active])

  return (
    <section className="container-tight py-20 md:py-28">
      <div className="max-w-2xl">
        <SectionEyebrow>FAQ</SectionEyebrow>
        <h2 className="mt-3 font-display text-display-2 text-balance">
          Frequently asked <span className="display-italic">questions</span>.
        </h2>
        <p className="mt-3 text-base text-muted-foreground md:text-lg">
          Everything you need to know.
        </p>
      </div>

      {/* Search input */}
      <div className="mt-8">
        <label className="relative block">
          <span className="sr-only">Search FAQs</span>
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions..."
            className="w-full rounded-full border bg-card py-3.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
          />
        </label>
      </div>

      {/* Category chips. Horizontal scroll on small screens so they
          never wrap awkwardly. */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {categories.map((c) => {
          const selected = active === c
          return (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                selected
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-card text-foreground/75 hover:border-foreground/40 hover:text-foreground'
              }`}
            >
              {c}
            </button>
          )
        })}
      </div>

      {/* Question list */}
      <div className="mt-8 space-y-3">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border bg-card p-10 text-center">
            <p className="text-sm text-muted-foreground">
              No questions match &ldquo;{query}&rdquo;. Try a broader term or clear the filter.
            </p>
          </div>
        ) : (
          filtered.map((f, i) => (
            <details
              key={f.question}
              className="group rounded-2xl border bg-card transition hover:border-foreground/20 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 sm:p-6">
                <div className="flex min-w-0 items-center gap-4">
                  <span className="hidden font-mono text-[11px] font-medium tabular-nums text-muted-foreground sm:inline-block sm:w-6">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-display text-base font-medium leading-snug text-foreground sm:text-lg">
                    {f.question}
                  </span>
                </div>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-foreground/65 transition group-open:rotate-45 group-open:border-foreground/40 group-open:text-foreground">
                  <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden>
                    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
              </summary>
              <div className="px-5 pb-6 pt-0 sm:px-6 sm:pl-[3.75rem]">
                <p className="text-pretty text-sm leading-relaxed text-muted-foreground sm:text-base">{f.answer}</p>
              </div>
            </details>
          ))
        )}
      </div>
    </section>
  )
}
